'use client';

import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import SplashScreen from '@/components/SplashScreen';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/components/ThemeProvider';
import { Sun, Moon } from 'lucide-react';
import BrandedLoader from '@/components/BrandedLoader';

export default function EntryPage() {
  const { user, loading: authLoading } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const router = useRouter();
  const [splashDone, setSplashDone] = useState(false);

  // After splash completes, if user is authenticated redirect to select-explorer
  useEffect(() => {
    if (splashDone && !authLoading && user) {
      router?.replace('/select-explorer');
    }
  }, [splashDone, authLoading, user, router]);

  if (!splashDone) {
    return <SplashScreen onComplete={() => setSplashDone(true)} duration={1400} />;
  }

  // If authenticated, show loading while redirecting
  if (authLoading || user) {
    return <BrandedLoader message="Getting things ready..." />;
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Subtle top bar */}
      <div className="flex items-center justify-between px-5 py-4 flex-shrink-0">
        <div className="flex items-center gap-2">
          <AppLogo size={32} />
          <span className="font-bold text-sm text-foreground tracking-tight">Naija Explorer</span>
        </div>
        <button
          onClick={toggleTheme}
          className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-all"
          aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
        >
          {theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
        </button>
      </div>

      {/* Main entry content — centered, one viewport */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-8 text-center">
        {/* Logo mark */}
        <div className="mb-8 animate-fade-in">
          <div className="w-28 h-28 rounded-3xl bg-primary/10 flex items-center justify-center mx-auto mb-5 border border-primary/15">
            <AppLogo size={68} />
          </div>
          <div className="flex items-center justify-center gap-1.5 mb-1">
            <div className="w-3 h-3 rounded-full bg-primary" />
            <div className="w-3 h-3 rounded-full bg-background border border-border" />
            <div className="w-3 h-3 rounded-full bg-primary" />
          </div>
        </div>

        {/* Headline */}
        <div className="mb-8 animate-slide-up">
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground leading-tight mb-3">
            Discover Nigeria.<br />
            <span className="text-primary">Discover Yourself.</span>
          </h1>
          <p className="text-muted-foreground text-base max-w-xs mx-auto leading-relaxed">
            An interactive learning journey through Nigeria&apos;s cultures, stories and languages.
          </p>
        </div>

        {/* CTA buttons */}
        <div className="w-full max-w-xs space-y-3 animate-slide-up" style={{ animationDelay: '0.1s' }}>
          <Link
            href="/signup"
            className="btn-primary w-full flex items-center justify-center gap-2 py-3.5 text-base font-semibold rounded-xl"
          >
            Get Started
          </Link>
          <Link
            href="/login"
            className="btn-secondary w-full flex items-center justify-center gap-2 py-3.5 text-base font-semibold rounded-xl"
          >
            I&apos;m a Parent — Sign In
          </Link>
        </div>

        {/* Subtle tagline */}
        <p className="mt-8 text-xs text-muted-foreground/60 animate-fade-in" style={{ animationDelay: '0.2s' }}>
          Safe · Educational · Nigerian-Made · Ages 6–14
        </p>
      </div>

      {/* Informational section — unauthenticated visitors only */}
      <section className="px-5 pb-8 pt-2 max-w-sm mx-auto w-full" aria-label="About Naija Explorer">
        <div className="border border-primary/15 rounded-2xl bg-primary/5 px-5 py-5">
          <h2 className="text-sm font-bold text-foreground leading-snug mb-2 text-center">
            A Fun Way for Children to Learn About Nigeria
          </h2>
          <p className="text-xs text-muted-foreground leading-relaxed text-center mb-5">
            Naija Explorer is an interactive educational experience for children ages 6–14 to discover Nigeria through its states, cultures, stories, languages and everyday life. Children can explore Nigerian history and traditions, learn Yoruba, Igbo and Hausa, build useful life skills, and reinforce what they learn through educational games and activities.
          </p>
          <div className="grid grid-cols-3 gap-2">
            <div className="flex flex-col items-center text-center gap-1.5">
              <div className="w-8 h-8 rounded-xl bg-primary/15 flex items-center justify-center flex-shrink-0">
                <span className="text-base" role="img" aria-hidden="true">🗺️</span>
              </div>
              <div>
                <h3 className="text-[10px] font-bold text-foreground leading-tight mb-0.5">Explore Nigeria</h3>
                <p className="text-[9px] text-muted-foreground leading-tight">Discover Nigeria&apos;s states, people, culture, history, food and traditions.</p>
              </div>
            </div>
            <div className="flex flex-col items-center text-center gap-1.5">
              <div className="w-8 h-8 rounded-xl bg-amber-500/15 flex items-center justify-center flex-shrink-0">
                <span className="text-base" role="img" aria-hidden="true">📖</span>
              </div>
              <div>
                <h3 className="text-[10px] font-bold text-foreground leading-tight mb-0.5">Stories &amp; Languages</h3>
                <p className="text-[9px] text-muted-foreground leading-tight">Enjoy Nigerian stories and begin learning Yoruba, Igbo and Hausa through child-friendly lessons.</p>
              </div>
            </div>
            <div className="flex flex-col items-center text-center gap-1.5">
              <div className="w-8 h-8 rounded-xl bg-primary/15 flex items-center justify-center flex-shrink-0">
                <span className="text-base" role="img" aria-hidden="true">🎮</span>
              </div>
              <div>
                <h3 className="text-[10px] font-bold text-foreground leading-tight mb-0.5">Games &amp; Life Skills</h3>
                <p className="text-[9px] text-muted-foreground leading-tight">Practice knowledge through educational games while building practical everyday skills.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Public navigation links */}
      <nav className="flex items-center justify-center gap-x-4 gap-y-1 flex-wrap px-5 pb-5 pt-1" aria-label="Public pages">
        <Link href="/about" className="text-[11px] text-muted-foreground/70 hover:text-primary transition-colors min-h-[44px] flex items-center">About</Link>
        <span className="text-muted-foreground/30 text-[11px]" aria-hidden="true">·</span>
        <Link href="/contact" className="text-[11px] text-muted-foreground/70 hover:text-primary transition-colors min-h-[44px] flex items-center">Contact</Link>
        <span className="text-muted-foreground/30 text-[11px]" aria-hidden="true">·</span>
        <Link href="/privacy" className="text-[11px] text-muted-foreground/70 hover:text-primary transition-colors min-h-[44px] flex items-center">Privacy</Link>
        <span className="text-muted-foreground/30 text-[11px]" aria-hidden="true">·</span>
        <Link href="/terms" className="text-[11px] text-muted-foreground/70 hover:text-primary transition-colors min-h-[44px] flex items-center">Terms</Link>
      </nav>

      {/* Nigerian flag accent at bottom */}
      <div className="h-1 flex flex-shrink-0">
        <div className="flex-1 bg-primary" />
        <div className="flex-1 bg-transparent" />
        <div className="flex-1 bg-primary" />
      </div>
    </div>
  );
}